import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';

import { AccountMenu } from '@/features/account/AccountMenu';
import { useLogout, useSession } from '@/features/auth/useSession';
import { useCartNavigation } from '@/features/procurement/useCartNavigation';
import { listApprovals } from '@/features/procurement/api';
import type { QuickFindPlace } from '@/features/search/goto';
import { QuickFindProvider, QuickFindTrigger } from '@/features/search/QuickFind';
import { identityKey } from '@/features/search/queryKeys';
import { forgetAllRecentSearches } from '@/features/search/recent';
import type { TenantFamily } from '@/features/search/types';
import { GuidanceRoot } from '@/guidance/GuidanceRoot';
import { HelpEntry } from '@/guidance/ui/HelpEntry';
import { Icon } from '@/shared/components/Icon';
import { useLocale } from '@/shared/i18n/useLocale';
import { usePermissions } from '@/shared/permissions/usePermissions';
import { useEffectiveAuthority } from '@/shared/permissions/useEffectiveAuthority';
import { PERMISSIONS, type PermissionSlug } from '@/shared/permissions/permissions';
import wordmark from '@/assets/brand/fidelen-logo-white.png';
import { Button } from '@/shared/components/Button';
import { Sheet } from '@/shared/components/Sheet';
import { cn } from '@/shared/utils/cn';
import { WorkspaceContent } from './WorkspaceContent';
import {
  ACCOUNT_MODULE,
  isCurrent,
  navigationModuleForPath,
  visibleNavigationModules,
  type NavItem,
  type NavigationModule,
} from './tenantNavigation';
import { ModuleNavigation, type ModuleNavigationGroup } from '@/shared/components/ModuleNavigation';
import { getReportCatalogue } from '@/features/reports/api';

/**
 * QF-14 / IA-16 — the "Start" commands Quick Find offers: the page each of
 * this seat's own create buttons opens, gated exactly as that button is.
 * Navigation only; the page asks before anything is saved.
 */
interface StartCommand {
  to: string;
  labelKey: string;
  /** Every one of these, as the page's own primary action requires. */
  permissions: PermissionSlug[];
}

const START_COMMANDS: StartCommand[] = [
  {
    // IA-16 — the off-catalogue need (CataloguePage / CatalogueItemPage gate).
    to: '/requests/off-catalogue/new',
    labelKey: 'catalogue.offCatalogueAction',
    permissions: [PERMISSIONS.requestOffCatalogue],
  },
  {
    // The enquiry form (StartRfqPage gate), for a seat that has Sourcing.
    to: '/sourcing/new',
    labelKey: 'sourcing.startRfq',
    permissions: [PERMISSIONS.rfqView, PERMISSIONS.rfqManage],
  },
];

const BOTTOM_NAV_PRIORITY = [
  '/dashboard',
  '/my/requests',
  '/approvals',
  '/catalogue',
  '/cart',
  '/receiving',
  '/purchase-orders',
];

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const { toggle, locale } = useLocale();
  const { pathname } = useLocation();
  // The shell holds its sign-out, so it re-renders as it settles (AccountMenu).
  const logout = useLogout();
  const { can, canAny } = usePermissions();
  const canReach = useEffectiveAuthority();
  const [menuOpen, setMenuOpen] = useState(false);
  const cartNavigation = useCartNavigation();
  const destinationOf = (item: NavItem) => (item.to === '/cart' ? cartNavigation.href : item.to);

  /*
   * Quick Find (CONTRACT §4) — what this seat may search, named in the entry
   * and the field so the placeholder says the real scope. The same permissions
   * gate the lists themselves; the server re-checks every family, with the
   * entitlement, before it queries, and leaves out any this seat may not read.
   * A seat that may search nothing has no entry at all.
   */
  const searchFamilies = useMemo<TenantFamily[]>(() => {
    const families: TenantFamily[] = [];
    if (can(PERMISSIONS.catalogueBrowse)) families.push('catalogue_items');
    if (canAny([PERMISSIONS.requestViewOwn, PERMISSIONS.requestViewAny])) families.push('requests');
    if (canAny([PERMISSIONS.orderViewBranch, PERMISSIONS.orderViewAny]))
      families.push('purchase_orders');
    if (can(PERMISSIONS.rfqView)) families.push('rfqs');
    if (can(PERMISSIONS.supplierView)) families.push('suppliers');

    return families;
  }, [can, canAny]);
  const searcher = identityKey([session?.tenant?.id, session?.user.id]);

  /*
   * Below 1024px the navigation is a Sheet (DS-02/DS-03): the platform dialog
   * traps focus inside it, Escape and the scrim close it, and focus returns to
   * the menu button — none of which the earlier full-screen overlay did beyond
   * Escape.
   */

  const visibleModules = visibleNavigationModules({ can, canAny, canReach });
  const visibleGroups = [...visibleModules, ACCOUNT_MODULE];
  const currentModule = navigationModuleForPath(pathname, visibleGroups);
  const [sheetModuleId, setSheetModuleId] = useState<string | null>(null);
  const sheetModule = visibleGroups.find((module) => module.id === sheetModuleId);
  const sheetBack = useRef<HTMLButtonElement>(null);
  const previousSheetModule = useRef<string | null>(null);
  useEffect(() => {
    if (menuOpen && sheetModuleId) sheetBack.current?.focus();
    if (menuOpen && !sheetModuleId && previousSheetModule.current) {
      document
        .querySelector<HTMLButtonElement>(
          `#mobile-navigation button[data-module-nav="${previousSheetModule.current}"]`,
        )
        ?.focus();
    }
    previousSheetModule.current = sheetModuleId;
  }, [menuOpen, sheetModuleId]);
  const openNavigation = (moduleId: string | null = null) => {
    setSheetModuleId(moduleId);
    setMenuOpen(true);
  };

  const labelOf = (item: NavItem): string =>
    item.wideLabel && canAny(item.wideLabel.anyPermission)
      ? item.wideLabel.labelKey
      : item.labelKey;

  const repeatsItsOnlyEntry = (group: NavigationModule): boolean =>
    group.items.length === 1 && t(labelOf(group.items[0])) === t(group.labelKey);

  /*
   * Quick Find's "Go to" (G-1): the very entries the sidebar shows this seat,
   * under the labels it shows, with their group where the heading is shown —
   * the account's own pages among them ("Your account", QF-13). The same
   * gates as the sidebar; nothing becomes reachable that is not. Each also
   * carries its label in the other language, to be found by either (QF-09).
   * Then the Start commands this seat's own buttons offer (QF-14).
   * Kept as one list while it says the same, so a re-render of the shell (the
   * approvals count arriving) leaves an open overlay's active row alone.
   */
  const tOther = i18n.getFixedT(locale === 'ar' ? 'en' : 'ar');
  const places = JSON.stringify([
    ...visibleGroups.flatMap((group) =>
      group.items.map((item): QuickFindPlace => ({
        to: destinationOf(item),
        label: t(labelOf(item)),
        altLabel: tOther(labelOf(item)),
        context: repeatsItsOnlyEntry(group) ? undefined : t(group.labelKey),
      })),
    ),
    ...START_COMMANDS.filter((command) => command.permissions.every((slug) => can(slug))).map(
      (command): QuickFindPlace => ({
        to: command.to,
        label: t(command.labelKey),
        altLabel: tOther(command.labelKey),
        kind: 'command',
      }),
    ),
  ]);
  const destinations = useMemo(() => JSON.parse(places) as QuickFindPlace[], [places]);

  /*
   * The mobile primary bar carries the first four destinations this person can
   * actually reach. It is derived from the same permission-filtered groups as
   * the sidebar — never a fixed list — so a seat that cannot reach a surface
   * has no tab for it, rather than a tab that refuses.
   */
  /*
   * The one badge the product can honestly show. `listApprovals` already reports
   * a total in its pagination meta, so `per_page: 1` asks for the count and one
   * row rather than a page of data — and it only runs for a seat that can see
   * approvals at all, so no other user pays for it.
   */
  const approvalsWaiting = useQuery({
    queryKey: ['approvals', 'nav-count'],
    queryFn: () => listApprovals({ per_page: 1 }),
    enabled: canReach('approvals'),
    staleTime: 60_000,
    // IA-06 — work that arrived while the person was in another tab is
    // counted when they come back (this query only; the product default
    // stays off).
    refetchOnWindowFocus: true,
  });

  const badgeCount = (name: NonNullable<NavItem['badge']>): number | null => {
    if (name === 'approvals') return approvalsWaiting.data?.meta.total ?? null;
    if (name === 'cart') return cartNavigation.count;

    return null;
  };

  /*
   * IA-06 — the pill says what the number is to a screen reader ("3 waiting
   * for you"), not a bare figure; the figure itself is the inbox's own total.
   */
  const badge = (item: NavItem, className: string) => {
    const count = item.badge ? badgeCount(item.badge) : null;

    if (count === null || count <= 0) return null;

    return (
      <span
        data-nav-badge=""
        key={item.badge === 'cart' ? cartNavigation.pulse : undefined}
        className={cn(
          className,
          item.badge === 'cart' &&
            cartNavigation.pulse > 0 &&
            'motion-safe:animate-[cart-confirmed_200ms_ease-out]',
        )}
      >
        <span aria-hidden="true">{count}</span>
        <span className="sr-only">
          {' '}
          {t(item.badge === 'cart' ? 'navigation.cartLines' : 'search.shell.waiting', { count })}
        </span>
      </span>
    );
  };

  /*
   * R-050 addendum 04 §Navigation: four destinations at most, the fourth being
   * More, promoted by DAILY FREQUENCY rather than sidebar order — Dashboard,
   * Requests, Approvals for the operational user. Only destinations this seat
   * can reach are candidates, so a requester's third tab is the catalogue, not
   * an approvals inbox that would refuse them. More opens the full navigation.
   */
  const reachable = visibleGroups.flatMap((group) => group.items);
  const bottomNavItems = BOTTOM_NAV_PRIORITY.flatMap((to) =>
    reachable.filter((item) => item.to === to),
  ).slice(0, 3);

  // Reuse ReportsLayout's server catalogue and query identity, only in Reports.
  const inReports =
    currentModule?.id === 'reports' &&
    (pathname === '/reports' || pathname.startsWith('/reports/'));
  const reportCatalogue = useQuery({
    queryKey: ['report-catalogue'],
    queryFn: getReportCatalogue,
    enabled: inReports && can(PERMISSIONS.reportView),
  });

  const childGroups = (module: NavigationModule, inSheet = false): ModuleNavigationGroup[] => {
    const groups: ModuleNavigationGroup[] = [
      {
        id: 'pages',
        items: module.items.map((item) => ({
          to: destinationOf(item),
          label: t(labelOf(item)),
          icon: item.icon,
          current: isCurrent(item, pathname)
            ? inReports && item.to === '/reports' && pathname !== '/reports'
              ? 'location'
              : 'page'
            : undefined,
          tour: inSheet ? undefined : item.tour,
          accessibleLabel:
            item.badge && (badgeCount(item.badge) ?? 0) > 0
              ? `${t(labelOf(item))} ${t(item.badge === 'cart' ? 'navigation.cartLines' : 'search.shell.waiting', { count: badgeCount(item.badge) })}`
              : undefined,
          trailing: badge(
            item,
            'numeric ms-auto rounded-full bg-exception px-2 py-0.5 type-label font-semibold text-ink-on-exception',
          ),
        })),
      },
    ];
    if (module.id === 'reports' && inReports) {
      const data = reportCatalogue.data?.data;
      if (data?.reports.length)
        groups.push({
          id: 'reports',
          label: t('reports.available'),
          items: data.reports.map((report) => ({
            to: `/reports/${report.id}`,
            label: report.title,
            code: report.requirement,
            current: pathname === `/reports/${report.id}` ? 'page' : undefined,
          })),
        });
      if (data?.datasets.length)
        groups.push({
          id: 'records',
          label: t('reports.records'),
          items: data.datasets.map((dataset) => ({
            to: `/reports/records/${dataset.id}`,
            label: dataset.title,
            current: pathname === `/reports/records/${dataset.id}` ? 'page' : undefined,
          })),
        });
    }
    return groups;
  };

  const brand = (
    <div className="pb-[18px] pt-[10px]">
      <img
        src={wordmark}
        alt={t('app.name')}
        width={96}
        height={31}
        className="block h-auto w-24"
      />
      {session?.tenant ? (
        <div className="mt-2.5 wrap-break-word ps-0.5 type-meta text-ink-on-shell-muted ltr:tracking-wide">
          {session.tenant.display_name}
        </div>
      ) : null}
    </div>
  );

  const moduleLabel = (module: NavigationModule) =>
    t(module.id === 'home' ? 'nav.dashboard' : module.labelKey);

  const primaryNavigation = (inSheet: boolean) => (
    <>
      {brand}
      <div className="mx-1 mb-3.5 h-px bg-line-on-shell" />
      <p className="type-label px-3 pb-2 text-ink-on-shell-muted">{t('navigation.workAreas')}</p>
      <ul className="space-y-1">
        {visibleModules.map((module) => {
          const selected = currentModule?.id === module.id;
          const waitingItem = module.items.find((item) => item.badge);
          const count = waitingItem?.badge ? badgeCount(waitingItem.badge) : null;
          const accessibleLabel =
            count && count > 0 && waitingItem
              ? `${moduleLabel(module)} — ${t(labelOf(waitingItem))} ${t(waitingItem.badge === 'cart' ? 'navigation.cartLines' : 'search.shell.waiting', { count })}`
              : undefined;
          const content = (
            <>
              <Icon name={module.icon} className={cn('shrink-0', selected && 'text-focus')} />
              <span className="min-w-0 flex-1 wrap-break-word">{moduleLabel(module)}</span>
              {waitingItem
                ? badge(
                    waitingItem,
                    'numeric rounded-full bg-exception px-2 py-0.5 type-label font-semibold text-ink-on-exception',
                  )
                : null}
              {inSheet ? <Icon name="chevron" className="shrink-0 rtl:rotate-180" /> : null}
            </>
          );
          const className = cn(
            // R-050 DEC-03: navigation is the Small role (14px), never an in-between size.
            'type-ui flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-start transition-colors focus-visible:outline-2 focus-visible:outline-focus',
            selected
              ? 'bg-shell-active font-medium text-ink-on-brand'
              : 'text-ink-on-shell-muted hover:bg-shell-hover hover:text-ink-on-shell',
          );
          return (
            <li key={module.id}>
              {inSheet ? (
                <button
                  type="button"
                  data-module-nav={module.id}
                  aria-label={accessibleLabel}
                  className={className}
                  onClick={() => setSheetModuleId(module.id)}
                >
                  {content}
                </button>
              ) : (
                <Link
                  to={destinationOf(module.items[0])}
                  data-module-nav={module.id}
                  aria-label={accessibleLabel}
                  aria-current={selected ? (module.id === 'home' ? 'page' : 'location') : undefined}
                  data-tour={module.id === 'home' ? module.items[0].tour : undefined}
                  className={className}
                >
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );

  const contextualNavigation = (module: NavigationModule, inSheet = false) => (
    <ModuleNavigation
      id={inSheet ? 'mobile-module-navigation' : 'module-navigation'}
      moduleId={module.id}
      label={t('navigation.moduleNavigation', { module: t(module.labelKey) })}
      title={t(module.labelKey)}
      groups={childGroups(module, inSheet)}
      tone={inSheet ? 'shell' : 'default'}
      responsive={!inSheet}
      tourId={!inSheet && inReports && module.id === 'reports' ? 'tenant.reports.rail' : undefined}
      onNavigate={() => setMenuOpen(false)}
      status={
        module.id === 'reports' && inReports && !reportCatalogue.data
          ? reportCatalogue.isError
            ? t('navigation.reportError')
            : t('navigation.reportLoading')
          : undefined
      }
    />
  );

  /*
   * Guidance lives INSIDE the authenticated shell: `RequireAuth` renders this
   * layout only for a complete session (MFA satisfied, no recovery codes held),
   * so Help, the invitation and the engine cannot exist on sign-in, MFA or a
   * secure link, and tear down with the shell on sign-out or expiry. The scope
   * is the opaque public ids of this tenant and this user — never a name.
   */
  return (
    <GuidanceRoot
      portal="tenant"
      scope={{ tenantId: session?.tenant?.id, userId: session?.user.id }}
    >
      {/* Keyed by who is searching: another identity starts with nothing. */}
      <QuickFindProvider
        key={searcher}
        portal="tenant"
        identity={searcher}
        families={searchFamilies}
        destinations={destinations}
      >
        <div
          className={cn(
            'min-h-dvh bg-canvas lg:flex',
            /*
             * DS-02/DS-03 — how much of the bottom edge the phone navigation takes,
             * for every BottomActionBar on the page to sit above: its height plus
             * the home-indicator area, which the navigation (not the bar) clears.
             */
            bottomNavItems.length > 0 &&
              '[--shell-bar-safe-area:0px] [--shell-nav-offset:calc(var(--layout-mobile-bottomnav-h)+env(safe-area-inset-bottom))]',
          )}
        >
          <a href="#main" className="skip-link">
            {t('a11y.skipToContent')}
          </a>

          {/*
           * R-050 shell sidebar — Deep Plum, 248px, sticky, full height. Active
           * item: Plum fill, white label, Focus Violet icon; hover one step up the
           * Plum ramp; 14% white hairlines; the count pill Orange with Deep Plum
           * text. Hidden below lg, where the same navigation opens in a Sheet.
           */}
          <nav
            id="primary-navigation"
            data-tour="tenant.shell.nav"
            aria-label={t('a11y.mainNavigation')}
            className="hidden w-[var(--layout-sidebar-w)] shrink-0 flex-col overflow-y-auto bg-shell px-[14px] py-[18px] lg:sticky lg:top-0 lg:flex lg:h-dvh"
          >
            {primaryNavigation(false)}
          </nav>

          <Sheet
            id="mobile-navigation"
            open={menuOpen}
            title={t('a11y.mainNavigation')}
            onClose={() => setMenuOpen(false)}
            side="start"
            hideTitle
            tone="shell"
          >
            <nav
              aria-label={t('a11y.mainNavigation')}
              className="flex flex-col px-[14px] pb-[18px]"
            >
              {sheetModule ? (
                <>
                  <button
                    ref={sheetBack}
                    type="button"
                    onClick={() => setSheetModuleId(null)}
                    className="mb-3 flex min-h-11 items-center gap-2 rounded-md px-3 text-start text-ink-on-shell focus-visible:outline-2 focus-visible:outline-focus"
                  >
                    <Icon name="chevron" className="rotate-180 rtl:rotate-0" />
                    {t('navigation.allWorkAreas')}
                  </button>
                  {contextualNavigation(sheetModule, true)}
                </>
              ) : (
                primaryNavigation(true)
              )}
            </nav>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col">
            {/*
             * Workspace utility bar — 64px. Global utilities live here, not in the
             * sidebar, and no second Fidelen identity mark is rendered.
             */}
            {/*
             * R12-01 — one line at 320px in Arabic, with Help in it: the text
             * buttons never wrap, and below 640px the bar's padding and gaps
             * tighten (the Help entry is an icon there, named "Help & tours").
             *
             * Quick Find (IA-04 / QF-03, one rule for the three shells): from
             * 1024px a field-shaped button at the start of the bar (the Golden
             * WorkspaceHeader's search slot), with its ⌘K / Ctrl K hint; below
             * that a 44px icon before Help, in the bar at every width from
             * 320px — no longer a row inside the menu sheet under 360px, which
             * Sign out moving into the account menu made room for.
             *
             * D-PUX-09 — the bar ends with the account menu: the person's
             * avatar (with their name from 1024px), holding My profile,
             * Sign-in security, Help & tours, the language and Sign out. Sign
             * out is no longer a header button; Help and the language stay in
             * the bar as before (T01's `tenant.shell.help`/`.language`).
             */}
            <header className="sticky top-0 z-30 flex h-[var(--layout-topbar-h)] flex-none items-center gap-1.5 border-b border-line-subtle bg-card px-3 sm:gap-4 sm:px-6">
              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap max-sm:px-2 lg:hidden"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                aria-controls="mobile-navigation"
                data-tour="tenant.shell.menu"
                onClick={() => openNavigation()}
              >
                {t('a11y.openMenu')}
              </Button>

              {/*
                From 1024px the Quick Find field is the part of the bar that
                gives way: it takes what the controls leave (at least a 44px
                target), and the controls keep their size, so no label — "Help
                & tours" in Arabic is the longest — ever wraps. Up to 720px, so
                that at a wide desk it names the seat's whole scope rather than
                an ellipsis beside empty bar (role 12, R12T-01/F4).
              */}
              <QuickFindTrigger
                shape="field"
                className="hidden min-w-11 max-w-[720px] flex-[1_1_480px] lg:flex"
              />

              <div className="ms-auto flex items-center gap-1.5 sm:gap-3.5 lg:shrink-0">
                <QuickFindTrigger shape="icon" className="lg:hidden" />

                {/* Help & tours — it carries its own anchor (tenant.shell.help). */}
                <HelpEntry className="whitespace-nowrap" />

                <Button
                  variant="ghost"
                  size="sm"
                  data-tour="tenant.shell.language"
                  onClick={toggle}
                  aria-label={t('a11y.toggleLanguage')}
                  className="min-w-11 justify-center whitespace-nowrap max-sm:px-2"
                >
                  <span lang={locale === 'ar' ? 'en' : 'ar'}>
                    {locale === 'ar' ? 'English' : 'العربية'}
                  </span>
                </Button>

                {/*
                  Who is signed in: the account menu's trigger, at every
                  width. Its name, with the role line under it (OBS-007),
                  shows from 1024px; below that the bar holds the menu,
                  Search, Help and the language, and the avatar alone (a 44px
                  target, named "Account: {name}") keeps it one line.
                */}
                <span className="hidden h-[22px] w-px bg-line-subtle lg:block" />

                <AccountMenu
                  portal="tenant"
                  signOut={{
                    run: () => {
                      // QF-07: this tab's recent searches go with the session.
                      forgetAllRecentSearches();
                      logout.mutate();
                    },
                    pending: logout.isPending,
                  }}
                />
              </div>
            </header>

            {currentModule && currentModule.id !== 'home' ? (
              <div className="border-b border-line-subtle bg-card px-3 py-2 sm:px-6 lg:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto min-h-11 max-w-full justify-start text-start"
                  aria-haspopup="dialog"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-navigation"
                  aria-label={t('navigation.openModule', { module: t(currentModule.labelKey) })}
                  onClick={() => openNavigation(currentModule.id)}
                >
                  <span className="min-w-0 wrap-break-word">
                    {t(currentModule.labelKey)}
                    <span className="block type-meta text-ink-muted">
                      {t(
                        labelOf(
                          currentModule.items.find((item) => isCurrent(item, pathname)) ??
                            currentModule.items[0],
                        ),
                      )}
                    </span>
                  </span>
                  <Icon name="chevron" className="ms-2 shrink-0 rtl:rotate-180" />
                </Button>
              </div>
            ) : null}

            <div className="flex min-w-0 flex-1 flex-col xl:flex-row">
              {currentModule && currentModule.id !== 'home' ? (
                <aside className="hidden border-b border-line-subtle bg-card px-4 py-4 lg:block xl:w-60 xl:shrink-0 xl:border-b-0 xl:border-e xl:px-3">
                  {contextualNavigation(currentModule)}
                </aside>
              ) : null}
              <main
                id="main"
                className="min-w-0 flex-1 py-6 pb-[calc(var(--layout-mobile-bottomnav-h)+env(safe-area-inset-bottom)+1.5rem)] lg:pb-6"
              >
                {/*
                 * Every page is centred inside R-050's `--layout-page-max`
                 * (DEC-GS-WIDTH); no screen sets a max-width of its own.
                 */}
                <WorkspaceContent>
                  <Outlet />
                </WorkspaceContent>
              </main>
            </div>

            {/* Mobile primary navigation. Real destinations only — see above. */}
            {bottomNavItems.length > 0 ? (
              <nav
                /*
                 * Its OWN accessible name. Two navigation landmarks sharing one
                 * name is ambiguous to a screen reader — and to anything else
                 * resolving a landmark by name.
                 */
                aria-label={t('a11y.quickNavigation')}
                data-tour="tenant.shell.bottom-nav"
                className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(var(--layout-mobile-bottomnav-h)+env(safe-area-inset-bottom))] items-stretch border-t border-line-subtle bg-card pb-[env(safe-area-inset-bottom)] lg:hidden"
              >
                {bottomNavItems.map((item) => {
                  const current = isCurrent(item, pathname);

                  return (
                    <Link
                      key={item.to}
                      to={destinationOf(item)}
                      aria-current={current ? 'page' : undefined}
                      aria-label={
                        item.badge && (badgeCount(item.badge) ?? 0) > 0
                          ? `${t(labelOf(item))} ${t(item.badge === 'cart' ? 'navigation.cartLines' : 'search.shell.waiting', { count: badgeCount(item.badge) })}`
                          : undefined
                      }
                      data-tour={item.tour}
                      className={cn(
                        'relative flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 type-label transition-colors',
                        current ? 'text-brand-600' : 'text-ink-muted hover:text-brand-600',
                      )}
                    >
                      {item.icon ? <Icon name={item.icon} className="shrink-0" /> : null}
                      <span className="max-w-full truncate">{t(labelOf(item))}</span>
                      {/* The entitlement-scoped count, Orange with Deep Plum text. */}
                      {badge(
                        item,
                        'numeric absolute top-1 start-[calc(50%+0.5rem)] rounded-full bg-exception px-1.5 type-label font-semibold text-ink-on-exception',
                      )}
                    </Link>
                  );
                })}
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-navigation"
                  data-tour="tenant.shell.more"
                  onClick={() => openNavigation()}
                  className="flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 type-label text-ink-muted hover:text-brand-600"
                >
                  <span aria-hidden="true" className="text-base leading-none">
                    ⋯
                  </span>
                  <span>{t('nav.more')}</span>
                </button>
              </nav>
            ) : null}
          </div>
        </div>
      </QuickFindProvider>
    </GuidanceRoot>
  );
}
